// The office shell's ringing banner (QA-726): a call that rings while the person is anywhere but
// Calls says who it is, on every office page, and opens the caller's panel on Calls. App holds the one
// ringing call it heard on the shell's stream; Calls itself pops the call in place, so the banner is
// never drawn there and a call never shows twice. It goes once the call is answered, ends or is opened.
import { useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import type { Call } from '../types.ts';
import { RING_FRESH_MS, ringingWords, stillRinging } from './ringing-model.ts';
import './ringing.css';

export function RingingBanner({
  callId,
  tick,
  onOpen,
  onGone,
}: {
  callId: string;
  /** Bumped when the stream says the call changed: read it again. */
  tick: number;
  /** Open the caller's panel on Calls. */
  onOpen: (callId: string) => void;
  /** It stopped ringing, or was put away. */
  onGone: () => void;
}) {
  const [call, setCall] = useState<Call | null>(null);
  // the shell's, read through a ref so a new one never reads the call again
  const gone = useRef(onGone);
  gone.current = onGone;
  useEffect(() => {
    let live = true;
    client
      .call<Call>('calls.get', { id: callId })
      .then((c) => {
        if (!live) return;
        if (stillRinging(c)) setCall(c);
        else gone.current();
      })
      .catch(() => live && gone.current());
    return () => {
      live = false;
    };
  }, [callId, tick]);
  // a ring nobody answers stops being one after its quarter hour, as on Calls
  useEffect(() => {
    if (!call) return;
    const left = Date.parse(call.startedAt) + RING_FRESH_MS - Date.now();
    const t = setTimeout(() => gone.current(), Math.max(0, left));
    return () => clearTimeout(t);
  }, [call]);
  if (!call || call.id !== callId) return null;
  return (
    <aside className="ring-banner" role="status" aria-label="A call is ringing">
      <p className="ring-banner-words">{ringingWords(call)}</p>
      <div className="ring-banner-acts">
        <button type="button" className="btn primary sm" onClick={() => onOpen(call.id)}>
          Open the call
        </button>
        <button type="button" className="btn quiet sm" onClick={onGone}>
          Not now
        </button>
      </div>
    </aside>
  );
}
